
import apiCall from './api';
import Bytes from 'bytes';
import i18n from './../i18n';
import { logger } from './logger';

const PROJECT_STATUS = Object.freeze({
  CANCELED: "CANCELED",
  COMPLETED: "COMPLETED",
  LIVE: "LIVE",
  PAUSED: "PAUSED",
  PLANNING: "PLANNING",
  SCHEDULED: "SCHEDULED",
});

const DRAFT_STATUS = Object.freeze({
  DRAFT: "DRAFT",
  LIVE: "LIVE",
  COMPLETED: "COMPLETED",
  CANCELED: "CANCELED",
  SCHEDULED: "SCHEDULED",
  PAUSED: "PAUSED",
});

const SUPPORTED_FILE_TYPES = Object.freeze([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'image/bmp',
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/tiff',
]);

/**
 * Task status enumeration.
 */
const TASK_STATUS = Object.freeze({
  AVAILABLE: "AVAILABLE",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  PAUSED: "PAUSED",
  CANCELED: "CANCELED",
  REJECTED: "REJECTED",
  UPCOMING: "UPCOMING"
});

function taskStatusToText(status) {
  if(status == null)
    return "";

  switch(status) {
    case TASK_STATUS.AVAILABLE: return i18n.global.t("task.status.available");
    case TASK_STATUS.IN_PROGRESS: return i18n.global.t("task.status.in_progress");
    case TASK_STATUS.COMPLETED: return i18n.global.t("task.status.completed");
    case TASK_STATUS.PAUSED: return i18n.global.t("task.status.paused");
    case TASK_STATUS.CANCELED: return i18n.global.t("task.status.canceled");
    case TASK_STATUS.REJECTED: return i18n.global.t("task.status.rejected");
    case TASK_STATUS.UPCOMING: return i18n.global.t("task.status.upcoming");
    default: return "";
  }
}

/**
 * Organization user roles enumeration.
 *
 * Do note that this should match server-side UserOrganizationRole enumeration.
 *
 * @see userRoleToText
 */
const USER_ROLES = Object.freeze({
  ORGANIZATION_ADMIN: "ORGANIZATION_ADMIN",
  GROUP_MANAGER: "GROUP_MANAGER",
  ORGANIZATION_MEMBER: "ORGANIZATION_MEMBER",
  GUEST: "GUEST",
  SIGN_ONLY: "SIGN_ONLY"
});

/**
 * Mapping from locale codes to locale names.
 *
 * See com.documill.platform.flow.controller.entity.Locale.LocaleCode
 */
const LOCALE_NAMES = Object.freeze({
  'en': 'English',
  'de': 'Deutsch',
  'sv': 'Svenska'
});

/**
 * Locales supported by Flow for the user language options.
 *
 * See com.documill.platform.flow.controller.entity.Locale.LocaleCode
 */
const SUPPORTED_LOCALES = Object.freeze(['en','de','sv']);

/**
 * Removes the file name suffix/extension.
 *
 * @param {String} fileName file name (may be null)
 *
 * @returns {String} file name without suffix (may be null)
 */

function getFileNameWithoutSuffix(fileName) {
  if(fileName == null)
    return null;

  return fileName.replace(/\.[^/.]+$/,"");
}

function getFileNameSuffix(fileName) {
 let lastDotIndex = fileName.lastIndexOf(".");
 return fileName.substring(lastDotIndex,fileName.length);
}

 /**
   * Validates the email format.
   *
   * @param {String} email
   *
   * @return {Boolean} is the email format valid
   */
 function validateEmail(email) {
  let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

  /**
   * Tests if an input value is considered valid.
   *
   * Value is valid if all the following requirements are met:
   * - Trimmed value is at least one character long
   * - Trimmed value does not contain non-ASCII characters
   */
  function isValidInput(value) {
    if(value == null)
      return false;

    // See DOS-2317 for more details.
    let trimmedValue = value.trim();
    if(trimmedValue.length < 1)
      return false;

    return /^[\x20-\xFF]*$/.test(trimmedValue);
  }

function getCollaboratorInitials(collaborator) {
  return (collaborator.email.substring(0,1)).toUpperCase();
}

function projectToDraftStatus(projectStatus) {

  switch(projectStatus) {
    case PROJECT_STATUS.CANCELED:
    case PROJECT_STATUS.COMPLETED:
    case PROJECT_STATUS.PAUSED:
    case PROJECT_STATUS.SCHEDULED:
    case PROJECT_STATUS.LIVE:
      return projectStatus;
    case PROJECT_STATUS.PLANNING:
      return DRAFT_STATUS.DRAFT;
    default:
      console.log("Invalid project status" + projectStatus);
      return projectStatus;
  }
}

 /**
 * Tests if file is supported by signing implementation.
 *
 * Currently supported content types are:
 * - DOCX
 * - PDF
 * - Image types: BMP, GIF, JPG, JPEG, PNG, TIFF (for more information see DOS-1331)
 *
 * See also SignService.java#isSupportedMIMEType method in the back-end. This function is
 * supposed to match that method.
 */
function isSupportedFileType(fileType) {
   return SUPPORTED_FILE_TYPES.includes(fileType);
}

function isDOCX(projectDocument) {
  return projectDocument.currentRevision.mimeType ==
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}

 /**
 * Gets the maximum allowed file size for uploaded files.
 *
  * @returns {Number} maximum file size in bytes (may be null)
*/
 function getMaxFileSize() {
  let maxFileSize = window.FlowSignOnlyWebsiteGlobalConfig.ui.maxFileSize;
  if(!maxFileSize) // No max file size defined.
    return null;

  let maxFileSizeBytes = Bytes.parse(maxFileSize);

  if(maxFileSizeBytes == null) { // Failed to parse?
    logger.error("Could not parse maxFileSize: " + maxFileSize);
    return null;
  }

  return maxFileSizeBytes;
}

/**
 * Converts an organization user role into human readable format.
 *
 * @param {?string} role to get human readable version of
 *
 * @return {string} human readable role name
 *
 * @see USER_ROLES
 */
function userRoleToText(role) {

  if(role) {
    switch(role) {
    case USER_ROLES.ORGANIZATION_ADMIN:
      return i18n.global.t('general.user-role.org-admin');
    case USER_ROLES.GROUP_MANAGER:
      return i18n.global.t('general.user-role.group-manager');
    case USER_ROLES.ORGANIZATION_MEMBER:
      return i18n.global.t('general.user-role.user');
    case USER_ROLES.GUEST:
      return i18n.global.t('general.user-role.guest');
    case USER_ROLES.SIGN_ONLY:
      return i18n.global.t('general.user-role.sign-only');
    default:
      break;
    }
  }

  return i18n.global.t('general.user-role.unknown');
}

/**
 * Downloads a specific Signed Document Revision as a file.
 *
 * Signed Document Revisions are E-signed versions of a specific Document
 * Revisions.
 *
 * Usage:
 *
 * In component script:
 * 1. import commons from "./../../utils/commons";
 * 2. commons.downloadSignedRevision(revisionId, fileName)
 */
async function downloadSignedRevision(revisionId, fileName) {
  let url = "/v1/documents/project/download/revision/signed/" + revisionId;

  await apiCall
    .get(url, { responseType: "blob" })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    })
    .catch(() => {
      logger.error("Error in downloading document revision.");
    });
}

export default {
  // Constants
  PROJECT_STATUS,
  TASK_STATUS,
  SUPPORTED_FILE_TYPES,
  USER_ROLES,
  LOCALE_NAMES,
  SUPPORTED_LOCALES,
  // --- Functions ---
  getMaxFileSize,
  getCollaboratorInitials,
  getFileNameSuffix,
  getFileNameWithoutSuffix,
  validateEmail,
  isValidInput,
  isSupportedFileType,
  isDOCX,
  projectToDraftStatus,
  userRoleToText,
  downloadSignedRevision,
  taskStatusToText
}
